import { App, Plugin, Route } from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import type { TestAppState } from "../app";
import { MagicLinkPage } from "../components/magic-link-page";
import { APP_ROUTES } from "../constants";
import { redeemMagicLink } from "../helpers/redeem-magic-link";

type RefreshLoginSession = () => Promise<boolean>;

export class MagicLinkPlugin implements Plugin<TestAppState> {
  route?: Route;
  private app?: App<TestAppState>;

  constructor(private readonly refreshLoginSession: RefreshLoginSession) {}

  onRegister(app: App<TestAppState>): void {
    this.app = app;

    const view = app.createView({ name: "MagicLink" });
    const page = view.addComponent({ component: MagicLinkPage });
    page.mapState(({ language, magicLinkFailed }) => ({
      language,
      hasError: Boolean(magicLinkFailed),
    }));
    this.route = app.createRoute({
      path: APP_ROUTES.magicLink,
      view,
      isOverlayChild: false,
    });
  }

  async redeemCurrentLink(): Promise<void> {
    if (!this.app) return;

    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      this.app.patchAppState({ isLoading: false });
      this.handleFailure(new Error("magic link token missing"));
      return;
    }

    this.app.patchAppState({ isLoading: true, magicLinkFailed: false });

    try {
      const result = await redeemMagicLink(token);
      const isLoggedIn = await this.refreshLoginSession();
      if (!isLoggedIn) {
        throw new Error("magic link session refresh failed");
      }

      this.app.navigate(result.redirectTo || APP_ROUTES.dashboard, {
        replace: true,
      });
    } catch (error) {
      this.handleFailure(error);
    } finally {
      this.app.patchAppState({ isLoading: false });
    }
  }

  private handleFailure(error: unknown): void {
    if (!this.app) return;

    console.error("Magic link redemption failed:", error);
    this.app.patchAppState({
      magicLinkFailed: true,
      snackbar: {
        message: getLocalizedMessage(
          this.app.getAppState().language,
          "magicLink.redeemFailed"
        ),
        severity: "error",
        open: true,
        type: "alert",
      },
    });
  }

  getName(): string {
    return "magic-link-plugin";
  }
}
